// app/src/utils/errorUtil.ts
import { AlertColor } from "@mui/material"

const GENERIC_ERROR_MESSAGE = "요청을 처리할 수 없습니다."

const statusCodeMessageMap: Record<number, string> = {
  400: "잘못된 요청입니다.",
  401: "로그인이 필요합니다.",
  403: "권한이 없습니다.",
  404: "대상을 찾을 수 없습니다.",
  408: "요청 시간이 초과되었습니다.",
  409: "이미 처리되었거나 충돌이 발생했습니다.",
  422: "입력값을 다시 확인해주세요.",
  429: "요청이 너무 많습니다. 잠시 후 다시 시도해주세요.",
  500: "서버 내부 오류가 발생했습니다.",
  502: "연결된 외부 서버 통신에 실패했습니다.",
  503: "서비스를 일시적으로 사용할 수 없습니다.",
  504: "외부 서버 응답 시간이 초과되었습니다.",
}

const extractStatusCode = (error: unknown): number | null => {
  const responseStatus = (error as any)?.response?.status
  if (typeof responseStatus === "number") {
    return responseStatus
  }

  const directMessage = typeof (error as any)?.message === "string" ? (error as any).message : ""
  const matched = directMessage.match(/(\d{3})/)
  if (!matched) {
    return null
  }

  const parsed = Number(matched[1])
  return Number.isFinite(parsed) ? parsed : null
}

export const resolveStatusCodeMessage = (statusCode: number | null | undefined): string | null => {
  if (typeof statusCode !== "number") {
    return null
  }

  return statusCodeMessageMap[statusCode] || `${statusCode} 오류가 발생했습니다.`
}

const errorMapping: Record<string, string> = {
  "This field may not be blank.": "이 항목을 채워주세요.",
  "이 필드는 blank일 수 없습니다.": "이 항목을 채워주세요.",
  "이 필드는 필수 항목입니다.": "이 항목을 채워주세요.",
  "이메일을 입력하세요.": "이메일을 입력해주세요.",
  "멤버의 이메일은/는 이미 존재합니다.": "이미 존재하는 이메일입니다.",
  "영어대문자, 소문자, 숫자, 특수문자중에서 2종류이상의 10자리 이상 또는 3종류 이상의 8자리 이상의 비밀번호를 입력하세요.":
    "영어대문자, 소문자, 숫자, 특수문자중에서 2종류이상의 10자리 이상 또는 3종류 이상의 8자리 이상의 비밀번호를 입력하세요.",
  "이전 비밀번호와 동일합니다. 다른 비밀번호를 사용하세요.": "이전 비밀번호와 동일합니다. 다른 비밀번호를 사용하세요.",
}

const normalizeMessage = (raw: any): string => {
  const text = String(raw ?? "").trim()
  const errorDetailMatch = text.match(/string='([^']+)'/)
  if (errorDetailMatch && errorDetailMatch[1]) {
    const normalized = errorDetailMatch[1].trim()
    return errorMapping[normalized] || normalized
  }

  // Django가 dict 문자열 전체를 message로 넘기는 경우 → 따옴표 안쪽 한글 메시지 추출 시도
  const braceMatch = text.match(/['"]([^'"]+['"]?)?([^'"]+)["']/)
  if (braceMatch && braceMatch[2]) {
    const candidate = braceMatch[2].trim()
    if (candidate) {
      return errorMapping[candidate] || candidate
    }
  }

  return errorMapping[text] || text || "알 수 없는 오류가 발생했습니다."
}

export const arrangeErrors = (error: unknown): Record<string, string[]> => {
  console.log("arrangeErrors - input error:", error)

  let errorData: any = null

  if (typeof error === "string") {
    errorData = error
  }

  if ((error as any)?.response?.data) {
    errorData = (error as any).response.data
  } else if ((error as any)?.data) {
    errorData = (error as any).data
  } else if (error && typeof error === "object") {
    errorData = error
  }

  console.log("arrangeErrors - parsed errorData:", errorData)

  const arrangedErrors: Record<string, string[]> = {}

  if (typeof errorData === "string") {
    arrangedErrors["detail"] = [normalizeMessage(errorData)]
    console.log("arrangeErrors - final result:", arrangedErrors)
    return arrangedErrors
  }

  if (errorData && typeof errorData === "object") {
    if (errorData.errors && typeof errorData.errors === "object") {
      for (const [field, fieldErrors] of Object.entries(errorData.errors)) {
        if (Array.isArray(fieldErrors)) {
          arrangedErrors[field] = fieldErrors.map(msg => normalizeMessage(msg))
        } else {
          arrangedErrors[field] = [normalizeMessage(fieldErrors)]
        }
      }
    } else {
      for (const [field, fieldValue] of Object.entries(errorData)) {
        if (field === "code" || field === "message") continue

        if (Array.isArray(fieldValue)) {
          arrangedErrors[field] = fieldValue.map(msg => normalizeMessage(msg))
        } else if (typeof fieldValue === "string") {
          arrangedErrors[field] = [normalizeMessage(fieldValue)]
        } else if (typeof fieldValue === "object" && fieldValue !== null) {
          const nestedErrors: string[] = []
          for (const [key, value] of Object.entries(fieldValue)) {
            if (typeof value === "string") {
              nestedErrors.push(normalizeMessage(value))
            } else if (Array.isArray(value)) {
              nestedErrors.push(...value.map(msg => normalizeMessage(msg)))
            }
          }
          if (nestedErrors.length > 0) {
            arrangedErrors[field] = nestedErrors
          }
        } else {
          arrangedErrors[field] = [normalizeMessage(fieldValue)]
        }
      }
    }
  }

  if (Object.keys(arrangedErrors).length === 0) {
    arrangedErrors["detail"] = ["알 수 없는 오류가 발생했습니다."]
  }

  console.log("arrangeErrors - final result:", arrangedErrors)
  return arrangedErrors
}

export const getFirstErrorMessage = (error: unknown): string => {
  console.log("getFirstErrorMessage - input error:", error)

  let errorData: any = null

  if (typeof error === "string") {
    const normalized = normalizeMessage(error)
    console.log("getFirstErrorMessage - using string error:", normalized)
    return normalized
  }

  // response.data 구조 확인
  if ((error as any)?.response?.data) {
    errorData = (error as any).response.data
  } else if ((error as any)?.data) {
    errorData = (error as any).data
  } else if (error && typeof error === "object") {
    errorData = error
  }

  console.log("getFirstErrorMessage - errorData:", errorData)

  // 1. validationErrors에서 첫 번째 에러 찾기 (handleApiError에서 추가한 필드)
  if ((error as any)?.validationErrors) {
    const validationErrors = (error as any).validationErrors
    console.log("getFirstErrorMessage - validationErrors:", validationErrors)

    for (const [field, messages] of Object.entries(validationErrors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        const firstMessage = messages[0]
        if (typeof firstMessage === "string" && firstMessage.trim()) {
          const normalized = normalizeMessage(firstMessage)
          console.log("getFirstErrorMessage - found validation error:", normalized)
          return normalized
        }
      } else if (typeof messages === "string" && messages.trim()) {
        const normalized = normalizeMessage(messages)
        console.log("getFirstErrorMessage - found validation error:", normalized)
        return normalized
      }
    }
  }

  // 2. errors 객체에서 찾기 (서버가 errors 필드로 내려주는 경우 최우선 사용)
  if (errorData?.errors && typeof errorData.errors === "object") {
    console.log("getFirstErrorMessage - checking errors object:", errorData.errors)

    for (const [field, messages] of Object.entries(errorData.errors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        const firstMessage = messages[0]
        if (typeof firstMessage === "string" && firstMessage.trim()) {
          const normalized = normalizeMessage(firstMessage)
          console.log("getFirstErrorMessage - found error in errors:", normalized)
          return normalized
        }
      } else if (typeof messages === "string" && messages.trim()) {
        const normalized = normalizeMessage(messages)
        console.log("getFirstErrorMessage - found error in errors:", normalized)
        return normalized
      }
    }
  }

  // 3. errorData.message가 있고, 의미있는 메시지인 경우 사용 (단, errors를 파싱하지 못했을 때만)
  if (errorData?.message && errorData.message !== "error" && errorData.message !== "validation_error" && typeof errorData.message === "string") {
    const trimmed = errorData.message.trim()
    // Django REST가 dict 문자열을 통째로 반환하는 경우는 제외
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      const normalized = normalizeMessage(errorData.message)
      console.log("getFirstErrorMessage - using errorData.message:", normalized)
      return normalized
    }
  }

  // 4. error.message가 있지만, axios 기본 에러가 아닌 경우에만 사용
  if (
    (error as any)?.message &&
    (error as any).message !== "error" &&
    (error as any).message !== "validation_error" &&
    typeof (error as any).message === "string" &&
    !(error as any)?.response // axios 에러가 아닌 경우에만
  ) {
    const normalized = normalizeMessage((error as any).message)
    console.log("getFirstErrorMessage - using error.message:", normalized)
    return normalized
  }

  // 5. arrangeErrors로 폴백
  const arrangedErrors = arrangeErrors(error)
  const errorValues = Object.values(arrangedErrors)

  for (const value of errorValues) {
    if (Array.isArray(value) && value.length > 0) {
      const firstError = value[0]
      if (typeof firstError === "string" && firstError.trim()) {
        console.log("getFirstErrorMessage - found first error from arrangeErrors:", firstError)
        return firstError
      }
    }
  }

  console.log("getFirstErrorMessage - returning default message")
  return GENERIC_ERROR_MESSAGE
}

const isMeaningfulMessage = (value: unknown): value is string =>
  typeof value === "string" &&
  value.trim().length > 0 &&
  value !== "error" &&
  value !== "validation_error" &&
  value !== "server_error" &&
  value !== "internal_server_error"

export const resolveApiErrorMessage = (error: unknown, fallbackMessage: string = GENERIC_ERROR_MESSAGE): string => {
  const responseMessage = (error as any)?.response?.data?.message ?? (error as any)?.data?.message
  if (isMeaningfulMessage(responseMessage)) {
    return normalizeMessage(responseMessage)
  }

  const firstErrorMessage = getFirstErrorMessage(error)
  if (firstErrorMessage && firstErrorMessage !== GENERIC_ERROR_MESSAGE) {
    return firstErrorMessage
  }

  const directMessage = (error as any)?.message
  if (isMeaningfulMessage(directMessage)) {
    const statusCodeMessage = resolveStatusCodeMessage(extractStatusCode(error))
    if (statusCodeMessage) {
      return statusCodeMessage
    }

    return normalizeMessage(directMessage)
  }

  const statusCodeMessage = resolveStatusCodeMessage(extractStatusCode(error))
  if (statusCodeMessage) {
    return statusCodeMessage
  }

  return fallbackMessage || GENERIC_ERROR_MESSAGE
}

export const showApiErrorSnackbar = (
  showSnackbar: (message: string, severity?: AlertColor) => void,
  error: unknown,
  fallbackMessage: string = GENERIC_ERROR_MESSAGE,
  severity: AlertColor = "error"
): string => {
  const message = resolveApiErrorMessage(error, fallbackMessage)
  showSnackbar(message, severity)
  return message
}

/**
 * 에러 응답에서 비밀번호 실패 횟수(failedAttempts) 추출
 * 서버에서 비밀번호 실패 시 { failedAttempts: number } 형태로 응답
 *
 * @param error - API 에러 객체
 * @returns 실패 횟수 (없으면 0)
 */
export const extractFailedAttempts = (error: unknown): number => {
  console.log("extractFailedAttempts - input error:", error)

  let errorData: any = null

  // response.data 구조 확인
  if ((error as any)?.response?.data) {
    errorData = (error as any).response.data
  } else if ((error as any)?.data) {
    errorData = (error as any).data
  } else if (error && typeof error === "object") {
    errorData = error
  }

  console.log("extractFailedAttempts - errorData:", errorData)

  // 직접 failedAttempts 필드가 있는 경우
  if (errorData?.failedAttempts !== undefined) {
    console.log("extractFailedAttempts - found failedAttempts:", errorData.failedAttempts)
    return Number(errorData.failedAttempts) || 0
  }

  // errors 객체 내부에 failedAttempts가 있는 경우
  if (errorData?.errors && typeof errorData.errors === "object") {
    for (const [field, value] of Object.entries(errorData.errors)) {
      if (typeof value === "object" && value !== null && (value as any).failedAttempts !== undefined) {
        console.log("extractFailedAttempts - found in errors:", (value as any).failedAttempts)
        return Number((value as any).failedAttempts) || 0
      }
    }
  }

  // validationErrors에서 찾기
  if ((error as any)?.validationErrors) {
    for (const [field, value] of Object.entries((error as any).validationErrors)) {
      if (typeof value === "object" && value !== null && (value as any).failedAttempts !== undefined) {
        console.log("extractFailedAttempts - found in validationErrors:", (value as any).failedAttempts)
        return Number((value as any).failedAttempts) || 0
      }
    }
  }

  console.log("extractFailedAttempts - not found, returning 0")
  return 0
}

